import { useCallback, useEffect, useMemo, useState } from 'react';

import { Form, Select } from 'antd';
import classNames from 'classnames';

import { EMPTY_MESSAGES, FORM_LABELS, FORM_RULES, MODAL_WIDTHS, VALIDATION_RULES } from '@/shared/lib/constants';
import { BaseModal } from '@/shared/ui/BaseModal';
import { DescriptionTextArea } from '@/shared/ui/DescriptionTextArea';
import { ExperimentTagInput } from '@/shared/ui/ExperimentTagInput';
import { TextInput } from '@/shared/ui/TextInput/TextInput';

import type { DagMaturity } from '@/entities/dag/domain/types';
import { useDataSources } from '@/entities/datasource';

import { DAG_ENTITY_TYPES, DAG_MATURITY_FORM_VALUES } from '@/features/dag-list/lib/dagCreateConstants';
import type { CreateDagFormValues } from '@/features/dag-list/model/dagCreateTypes';
import { useCreateDagMutation } from '@/features/dag-list/model/useCreateDagMutation';
import styles from '@/features/dag-list/ui/CreateDagModal/CreateDagModal.module.css';

const CREATE_DAG_FORM_ID = 'create-dag-form';

const DAG_NAME_RULES = [
  ...FORM_RULES.REQUIRED_NAME,
  {
    pattern: /^[a-z_][a-z0-9_]*$/,
    message: '첫 글자는 소문자 또는 _만 가능하며, 이후에는 소문자, 숫자, _만 사용할 수 있습니다',
  },
  {
    max: 50,
    message: '최대 50자까지 입력 가능합니다',
  },
];
const MATURITY_RULES = [{ required: true, message: 'Maturity를 선택해 주세요' }];
const DATASOURCE_RULES = [{ required: true, message: '데이터 소스를 선택해 주세요' }];
const ENTITY_TYPE_RULES = [{ required: true, message: 'Entity 유형을 선택해 주세요' }];

const DESCRIPTION_RULES = [
  ...FORM_RULES.REQUIRED_DESCRIPTION,
  {
    max: VALIDATION_RULES.DESCRIPTION_MAX_LENGTH,
    message: '최대 300자까지 입력 가능합니다',
  },
];

const TAGS_LIST_RULES = [
  {
    validator: (_: unknown, value: unknown) => {
      if (normalizeTags(value).length === 0) {
        return Promise.reject(new Error('태그를 하나 이상 입력해 주세요'));
      }

      return Promise.resolve();
    },
  },
];

const MATURITY_SELECT_OPTIONS = [
  { label: 'Bronze', value: DAG_MATURITY_FORM_VALUES.BRONZE },
  { label: 'Silver', value: DAG_MATURITY_FORM_VALUES.SILVER },
  { label: 'Gold', value: DAG_MATURITY_FORM_VALUES.GOLD },
];

const ENTITY_TYPE_SELECT_OPTIONS = [
  { label: '데이터셋', value: DAG_ENTITY_TYPES.DATASET },
  { label: '피처셋', value: DAG_ENTITY_TYPES.FEATURE_SET },
];

const normalizeTags = (tags?: unknown) =>
  (Array.isArray(tags) ? tags : [])
    .map((tag) => (typeof tag === 'string' ? tag.trim() : ''))
    .filter((tag): tag is string => tag.length > 0);

type CreateDagModalProps = {
  open: boolean;
  onClose: () => void;
};

type FieldLabelProps = {
  label: string;
  required?: boolean;
};

const FieldLabel = ({ label, required = false }: FieldLabelProps) => (
  <div className={styles.fieldLabel}>
    <span>{label}</span>
    {required && <span className={styles.requiredDot} aria-hidden="true" />}
  </div>
);

export const CreateDagModal = ({ open, onClose }: CreateDagModalProps) => {
  const [form] = Form.useForm<CreateDagFormValues>();
  const { mutate: createDag, isPending: isCreatePending } = useCreateDagMutation();
  const [isTagsValid, setIsTagsValid] = useState(true);

  const maturity = Form.useWatch('maturity', form);
  const name = Form.useWatch('name', form);
  const datasourceId = Form.useWatch('datasourceId', form);
  const entityType = Form.useWatch('entityType', form);
  const description = Form.useWatch('description', form);
  const tags = Form.useWatch('tags', form);

  const {
    data: datasourceResponse,
    isLoading: isDatasourceLoading,
    isFetching: isDatasourceFetching,
  } = useDataSources(
    {
      maturity: maturity as DagMaturity,
    },
    {
      enabled: open && maturity !== undefined,
    }
  );

  const datasourceOptions = useMemo(
    () =>
      (datasourceResponse?.items ?? []).map((item) => ({
        value: item.id,
        label: item.name,
      })),
    [datasourceResponse]
  );

  const isGold = maturity === DAG_MATURITY_FORM_VALUES.GOLD;

  const isDatasourceListEmpty =
    !isDatasourceLoading && !isDatasourceFetching && maturity !== undefined && datasourceOptions.length === 0;

  const trimmedDescriptionLength = typeof description === 'string' ? description.trim().length : 0;
  const isDescriptionValid =
    typeof description === 'string' &&
    trimmedDescriptionLength >= VALIDATION_RULES.DESCRIPTION_MIN_LENGTH &&
    trimmedDescriptionLength <= VALIDATION_RULES.DESCRIPTION_MAX_LENGTH;

  const resetForm = useCallback(() => {
    form.resetFields();
  }, [form]);

  useEffect(() => {
    if (open) {
      form.resetFields();
      setIsTagsValid(true);
    }
  }, [open, form]);

  useEffect(() => {
    form.setFieldValue('datasourceId', undefined);
    form.setFieldValue('entityType', undefined);
  }, [maturity, form]);

  const handleCloseIcon = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const isDescriptionTouched = description !== undefined && description !== '';
  const hasDescriptionMinError =
    isDescriptionTouched && trimmedDescriptionLength < VALIDATION_RULES.DESCRIPTION_MIN_LENGTH;
  const hasDescriptionMaxError =
    isDescriptionTouched && trimmedDescriptionLength > VALIDATION_RULES.DESCRIPTION_MAX_LENGTH;
  const showDescriptionError = hasDescriptionMinError || hasDescriptionMaxError;
  const hasValidTag = normalizeTags(tags).length > 0;

  const isSubmitDisabled = useMemo(() => {
    if (isCreatePending || isDatasourceLoading || isDatasourceFetching) return true;
    if (!name?.trim() || maturity === undefined) return true;
    if (isDatasourceListEmpty) return true;
    if (!datasourceId) return true;
    if (isGold && !entityType) return true;
    if (!isDescriptionValid) return true;
    if (!hasValidTag) return true;
    if (!isTagsValid) return true;

    return false;
  }, [
    datasourceId,
    entityType,
    hasValidTag,
    isCreatePending,
    isDatasourceFetching,
    isDatasourceListEmpty,
    isDatasourceLoading,
    isDescriptionValid,
    isGold,
    isTagsValid,
    maturity,
    name,
  ]);

  const handleSubmit = useCallback(
    (values: CreateDagFormValues) => {
      const validTags = normalizeTags(values.tags);

      createDag(
        {
          name: values.name.trim(),
          maturity: values.maturity,
          dataSourceId: values.datasourceId,
          description: values.description.trim(),
          ...(values.maturity === DAG_MATURITY_FORM_VALUES.GOLD && values.entityType
            ? { entityType: values.entityType }
            : {}),
          tags: validTags,
        },
        {
          onSuccess: () => {
            handleCloseIcon();
          },
        }
      );
    },
    [createDag, handleCloseIcon]
  );

  const selectCommonProps = {
    className: styles.select,
    disabled: isCreatePending,
    showSearch: false,
    optionFilterProp: 'label' as const,
  };

  return (
    <BaseModal
      width={MODAL_WIDTHS.STANDARD_FORM}
      title="DAG 생성"
      open={open}
      onCloseIcon={handleCloseIcon}
      onClose={handleCloseIcon}
      onOk={() => form.submit()}
      okText="생성"
      cancelText="취소"
      confirmLoading={isCreatePending}
      okButtonProps={{ disabled: isSubmitDisabled, loading: isCreatePending }}>
      <Form
        id={CREATE_DAG_FORM_ID}
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className={styles.form}
        initialValues={{ tags: [''] }}>
        <div className={styles.field}>
          <FieldLabel label="Name" required />
          <Form.Item
            name="name"
            rules={DAG_NAME_RULES}
            validateFirst
            className={classNames(styles.controlItem, styles.nameInput)}>
            <TextInput
              className={styles.input}
              placeholder="이름을 입력해 주세요"
              disabled={isCreatePending}
              maxLength={50}
            />
          </Form.Item>
        </div>

        <div className={styles.row}>
          <div className={styles.field}>
            <FieldLabel label="Maturity" required />
            <Form.Item
              name="maturity"
              rules={MATURITY_RULES}
              className={classNames(styles.controlItem, styles.noneMessage)}>
              <Select
                placeholder="선택해 주세요"
                options={MATURITY_SELECT_OPTIONS}
                aria-label="Maturity"
                autoClearSearchValue={false}
                {...selectCommonProps}
              />
            </Form.Item>
          </div>

          <div className={styles.field}>
            <FieldLabel label="Entity Type" required />
            <Form.Item
              name="entityType"
              rules={isGold ? ENTITY_TYPE_RULES : undefined}
              className={classNames(styles.controlItem, styles.noneMessage)}>
              <Select
                className={styles.select}
                placeholder="선택해 주세요"
                options={ENTITY_TYPE_SELECT_OPTIONS}
                aria-label="Entity Type"
                disabled={isCreatePending || !isGold}
                showSearch={false}
                optionFilterProp="label"
              />
            </Form.Item>
          </div>
        </div>

        <div className={styles.field}>
          <FieldLabel label="Data Source" required />
          <Form.Item
            name="datasourceId"
            rules={DATASOURCE_RULES}
            className={classNames(styles.controlItem, styles.noneMessage)}>
            <Select
              className={styles.select}
              placeholder="선택해 주세요"
              options={datasourceOptions}
              loading={isDatasourceLoading || isDatasourceFetching}
              disabled={isCreatePending || maturity === undefined}
              showSearch
              optionFilterProp="label"
              aria-label="Data Source"
              notFoundContent={EMPTY_MESSAGES.NO_DAG_DATA_SOURCES}
            />
          </Form.Item>
        </div>

        <div className={styles.field}>
          <FieldLabel label="Description" required />
          <Form.Item
            name="description"
            rules={DESCRIPTION_RULES}
            validateFirst
            className={classNames(styles.controlItem, styles.noneMessage)}>
            <DescriptionTextArea
              placeholder="설명을 입력해 주세요 (최소 30자 이상)"
              maxLength={300}
              minLength={VALIDATION_RULES.DESCRIPTION_MIN_LENGTH}
              showHelper
              status={showDescriptionError ? 'error' : undefined}
              aria-label={FORM_LABELS.DESCRIPTION}
            />
          </Form.Item>
        </div>

        <div className={styles.field}>
          <FieldLabel label="Tags" required />
          <Form.Item name="tags" rules={TAGS_LIST_RULES} className={classNames(styles.controlItem, styles.noneMessage)}>
            <ExperimentTagInput minTags={1} showAddButton onValidationChange={setIsTagsValid} />
          </Form.Item>
        </div>
      </Form>
    </BaseModal>
  );
};
